import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { DeleteRunDialog } from '../../components/common/DeleteRunDialog';
import { MetricsSummary } from '../../components/common/MetricsSummary';
import { ModelTypeBadge } from '../../components/common/ModelTypeBadge';
import { ProgressBar } from '../../components/common/ProgressBar';
import { RunTypeBadge } from '../../components/common/RunTypeBadge';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useRunProgress } from '../../hooks/useRunProgress';
import { ALL_METRICS, fmtPct, resultSummary } from '../../metrics';
import { EvalApiService } from '../../services/EvalApiService';
import {
  ACTIVE_RUN_STATUSES,
  EvalRun,
  EvalRunInference,
  RETRYABLE_RUN_STATUSES,
  RunResult,
  RunType,
} from '../../types';
import { fmtDuration, fmtIso } from '../../utils';

function MetricsCell({ metrics, runType }: { metrics: RunResult; runType: RunType }) {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const summary = resultSummary(metrics);

  const rows: Array<[string, string]> = ALL_METRICS.filter((m) => {
    if (m.section === 'OD') return !!metrics.OD;
    if (m.section === 'PR') return !!metrics.PR;
    if (m.section === 'PC') return !!metrics.PC;
    return false;
  }).map((m) => [m.label, (m.fmt ?? fmtPct)(m.getValue(metrics))]);

  return (
    <div
      style={{ display: 'inline-block' }}
      onMouseEnter={(e) => setAnchorRect(e.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => setAnchorRect(null)}
    >
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          borderBottom: rows.length ? '1px dashed var(--text-secondary)' : undefined,
          cursor: rows.length ? 'default' : undefined,
        }}
      >
        {summary}
      </span>
      {anchorRect && rows.length > 0 && (
        <div
          style={{
            position: 'fixed',
            ...(window.innerHeight - anchorRect.bottom < 180
              ? { bottom: window.innerHeight - anchorRect.top + 6 }
              : { top: anchorRect.bottom + 6 }),
            left: anchorRect.left,
            zIndex: 9999,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '8px 10px',
            minWidth: 160,
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
          }}
        >
          {rows.map(([label, value]) => (
            <div
              key={label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                fontSize: 11,
                marginBottom: 3,
              }}
            >
              <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                {value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface RunDetailContentProps {
  runId: string;
  token: string;
}

const RunDetailContent: React.FC<RunDetailContentProps> = ({ runId, token }) => {
  const navigate = useNavigate();
  const { showErrorToast } = useNotificationContext();
  const [run, setRun] = useState<EvalRun | null>(null);
  const [sourceRun, setSourceRun] = useState<EvalRun | null>(null);
  const [inferences, setInferences] = useState<EvalRunInference[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingComment, setEditingComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  const isLive = run != null && ACTIVE_RUN_STATUSES.has(run.status);
  const { run: liveRun, progress } = useRunProgress(isLive ? runId : '', token);

  useEffect(() => {
    Promise.all([
      EvalApiService.getRun(token, runId),
      EvalApiService.getRunInferences(token, runId),
    ]).then(([fetchedRun, fetchedInferences]) => {
      setRun(fetchedRun);
      setInferences(fetchedInferences.items);
      setLoading(false);
    });
  }, [runId, token]);

  useEffect(() => {
    if (liveRun) setRun(liveRun);
  }, [liveRun]);

  useEffect(() => {
    if (!liveRun) return;
    EvalApiService.getRunInferences(token, runId).then((r) => setInferences(r.items));
  }, [liveRun, runId, token]);

  useEffect(() => {
    if (!run?.source_run_id) {
      setSourceRun(null);
      return;
    }
    EvalApiService.getRun(token, run.source_run_id).then(setSourceRun);
  }, [run?.source_run_id, token]);

  const handleCancel = async () => {
    if (!run) return;
    const updated = await EvalApiService.cancelRun(token, run.run_id);
    setRun(updated);
  };

  const handleRetry = async () => {
    if (!run) return;
    const updated = await EvalApiService.retryRun(token, run.run_id);
    setRun(updated);
    navigate(`/eval/runs/${updated.run_id}`);
  };

  const handleDelete = async () => {
    if (!run) return;
    await EvalApiService.deleteRun(token, run.run_id);
    navigate('/eval/runs');
  };

  const openEditComment = () => {
    setCommentDraft(run?.comment ?? '');
    setEditingComment(true);
  };

  const handleSaveComment = async () => {
    if (!run) return;
    setSavingComment(true);
    try {
      const updated = await EvalApiService.patchRunComment(
        token,
        run.run_id,
        commentDraft.trim() || null
      );
      setRun(updated);
      setEditingComment(false);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to save comment');
    } finally {
      setSavingComment(false);
    }
  };

  if (loading) {
    return <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>;
  }

  if (!run) {
    return <div style={{ color: 'var(--danger)', fontSize: 13 }}>Run not found.</div>;
  }

  const canCancel = ACTIVE_RUN_STATUSES.has(run.status);
  const canRetry = RETRYABLE_RUN_STATUSES.has(run.status);
  const canDelete = !canCancel;

  return (
    <>
      {showDeleteDialog && (
        <DeleteRunDialog
          runIds={[run.run_id]}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}
      <div className="eval-page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="eval-page-title" style={{ fontFamily: 'var(--font-mono)' }}>
              {run.run_id ?? runId}
            </div>
            <RunTypeBadge runType={run.run_type} />
            <StatusBadge status={run.status} />
          </div>
          <div className="eval-page-sub">
            Triggered by {run.triggered_by} &middot; {fmtIso(run.triggered_at)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canCancel && (
            <button className="eval-btn-danger" onClick={handleCancel}>
              Cancel Run
            </button>
          )}
          {canRetry && (
            <button className="eval-btn-warning" onClick={handleRetry}>
              Retry Run
            </button>
          )}
          {canDelete && (
            <button className="eval-btn-danger" onClick={() => setShowDeleteDialog(true)}>
              Delete Run
            </button>
          )}
        </div>
      </div>

      {run.reason && (
        <div
          className="eval-reason-box"
          data-variant={run.status === 'timed_out' ? 'warning' : undefined}
        >
          {run.reason}
        </div>
      )}

      {isLive && progress && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Processing inferences...
          </div>
          <ProgressBar completed={progress.completed} total={progress.total} />
        </div>
      )}

      <div
        style={{
          marginBottom: 24,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 12,
        }}
      >
        {run.model_ids.OD && (
          <div className="eval-info-card">
            <div className="eval-info-label">
              <ModelTypeBadge modelType="OD" />
            </div>
            <div className="eval-info-value">{run.model_ids.OD}</div>
          </div>
        )}
        {run.model_ids.PR && (
          <div className="eval-info-card">
            <div className="eval-info-label">
              <ModelTypeBadge modelType="PR" />
            </div>
            <div className="eval-info-value">{run.model_ids.PR}</div>
          </div>
        )}
        {run.model_ids.PC && (
          <div className="eval-info-card">
            <div className="eval-info-label">
              <ModelTypeBadge modelType="PC" />
            </div>
            <div className="eval-info-value">{run.model_ids.PC}</div>
          </div>
        )}
        <div className="eval-info-card">
          <div className="eval-info-label">Dataset</div>
          <div className="eval-info-value">
            <Link to={`/eval/datasets/${run.dataset_id}`} style={{ color: 'var(--accent)' }}>
              {run.dataset_name ?? run.dataset_id}
            </Link>
            {run.dataset_name && (
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  marginTop: 2,
                }}
              >
                {run.dataset_id}
              </div>
            )}
          </div>
        </div>
        {run.source_run_id && (
          <div className="eval-info-card">
            <div className="eval-info-label">Base Run</div>
            <div className="eval-info-value">
              <Link to={`/eval/runs/${run.source_run_id}`} style={{ color: 'var(--accent)' }}>
                {sourceRun
                  ? ((run.run_type === 'PR' ? sourceRun.model_ids.OD : sourceRun.model_ids.PR) ??
                    run.source_run_id)
                  : run.source_run_id}
              </Link>
              {sourceRun &&
                (run.run_type === 'PR' ? sourceRun.model_ids.OD : sourceRun.model_ids.PR) && (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                      marginTop: 2,
                    }}
                  >
                    {run.source_run_id}
                  </div>
                )}
              {sourceRun && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
                  {fmtIso(sourceRun.triggered_at)}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="eval-info-card">
          <div className="eval-info-label">Duration</div>
          <div className="eval-info-value">{fmtDuration(run.started_at, run.completed_at)}</div>
        </div>
        <div className="eval-info-card">
          <div className="eval-info-label">Triggered</div>
          <div className="eval-info-value">{fmtIso(run.triggered_at)}</div>
        </div>
        {run.started_at && (
          <div className="eval-info-card">
            <div className="eval-info-label">Started</div>
            <div className="eval-info-value">{fmtIso(run.started_at)}</div>
          </div>
        )}
        {run.completed_at && (
          <div className="eval-info-card">
            <div className="eval-info-label">Completed</div>
            <div className="eval-info-value">{fmtIso(run.completed_at)}</div>
          </div>
        )}
        {run.config && Object.keys(run.config).length > 0 && (
          <div className="eval-info-card" style={{ gridColumn: '1 / -1' }}>
            <div className="eval-info-label">Config</div>
            <div
              className="eval-info-value"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}
            >
              {JSON.stringify(run.config)}
            </div>
          </div>
        )}
        {!ACTIVE_RUN_STATUSES.has(run.status) && (
          <div className="eval-info-card" style={{ gridColumn: '1 / -1' }}>
            <div
              className="eval-info-label"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              Notes
              {!editingComment && (
                <button
                  onClick={openEditComment}
                  className="eval-btn-ghost"
                  style={{ fontSize: 11, padding: '2px 8px' }}
                >
                  {run.comment ? 'Edit' : 'Add'}
                </button>
              )}
            </div>
            <div className="eval-info-value">
              {editingComment ? (
                <div>
                  <textarea
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Add a note about this run…"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      background: 'var(--surface)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '6px 8px',
                      fontSize: 13,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button
                      className="eval-btn"
                      onClick={handleSaveComment}
                      disabled={savingComment}
                    >
                      {savingComment ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      className="eval-btn-ghost"
                      onClick={() => setEditingComment(false)}
                      disabled={savingComment}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <span
                  style={{
                    color: run.comment ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontStyle: run.comment ? 'normal' : 'italic',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {run.comment ?? 'No notes.'}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: 12,
          }}
        >
          Metrics
        </div>
        <MetricsSummary result={run.result} runType={run.run_type} />
      </div>

      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: 12,
          }}
        >
          Per-Image Results
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Sample</th>
              <th>GT Inference</th>
              <th>Metrics</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {inferences.map((inf) => (
              <tr key={inf.run_inference_id} style={{ cursor: 'pointer' }}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                  <Link
                    to={`/eval/runs/${runId}/inferences/${inf.run_inference_id}?gt=${inf.gt_sample_id}`}
                    style={{ color: 'var(--accent)' }}
                  >
                    {inf.run_inference_id}
                  </Link>
                </td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{inf.gt_sample_id}</td>
                <td>
                  <MetricsCell metrics={inf.metrics} runType={run.run_type} />
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {fmtIso(inf.created_at)}
                </td>
              </tr>
            ))}
            {inferences.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}
                >
                  No per-image results yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export const RunDetailPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { token } = useAuthContext();

  return (
    <div className="page-content">
      {runId && token ? (
        <RunDetailContent runId={runId} token={token} />
      ) : (
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>Invalid run ID.</div>
      )}
    </div>
  );
};
